import React from 'react';
import { LocationPicker } from './LocationPicker';
import { Plus, Trash2, MapPin } from 'lucide-react';

interface Coordinates {
  latitude: number;
  longitude: number;
  address?: string;
}

interface MultiLocationPickerProps {
  label: string;
  description?: string;
  value: Coordinates[];
  onChange: (locations: Coordinates[]) => void;
  disabled?: boolean;
  maxLocations?: number;
  placeholder?: string;
}

export const MultiLocationPicker: React.FC<MultiLocationPickerProps> = ({
  label,
  description,
  value,
  onChange,
  disabled = false,
  maxLocations = 10,
  placeholder = "Ajouter une localisation"
}) => {
  const handleLocationChange = async (index: number, coordinates: Coordinates) => {
    const newLocations = [...value];
    newLocations[index] = coordinates;
    onChange(newLocations);
  };

  const handleRemoveLocation = (index: number) => {
    const newLocations = value.filter((_, i) => i !== index);
    onChange(newLocations);
  };

  const handleAddNewLocation = async (coordinates: Coordinates) => {
    if (value.length >= maxLocations) return;
    const newLocations = [...value, coordinates];
    onChange(newLocations);
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
        {description && (
          <p className="text-sm text-gray-500 mb-3">{description}</p>
        )}
      </div>

      {/* Liste des localisations existantes */}
      <div className="space-y-3">
        {value.map((location, index) => (
          <div key={index} className="relative">
            <div className="flex items-start space-x-3">
              <div className="flex-1">
                <LocationPicker
                  label={`${label} #${index + 1}`}
                  value={location}
                  onSave={(coords) => handleLocationChange(index, coords)}
                  disabled={disabled}
                  placeholder={`Sélectionner ${label.toLowerCase()} #${index + 1}`}
                />
              </div>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemoveLocation(index)}
                  className="mt-7 p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                  title="Supprimer cette localisation"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Bouton pour ajouter une nouvelle localisation */}
      {!disabled && value.length < maxLocations && (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
          <button
            type="button"
            onClick={() => {
              // Créer un LocationPicker temporaire pour l'ajout
              const tempLocation = { latitude: -1.6792, longitude: 29.2228, address: "Position temporaire" };
              handleAddNewLocation(tempLocation);
            }}
            className="w-full flex items-center justify-center space-x-2 py-4 text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <Plus className="w-5 h-5" />
            <span>{placeholder}</span>
          </button>
        </div>
      )}

      {/* Indication du nombre max */}
      {value.length > 0 && (
        <div className="flex items-center justify-between text-sm text-gray-500">
          <div className="flex items-center space-x-1">
            <MapPin className="w-4 h-4" />
            <span>{value.length} localisation{value.length > 1 ? 's' : ''} ajoutée{value.length > 1 ? 's' : ''}</span>
          </div>
          {maxLocations && (
            <span>
              {value.length}/{maxLocations} max
            </span>
          )}
        </div>
      )}
    </div>
  );
};